import type { DashboardActionClientContext } from './dashboard-execution.ts';
import type { ToolContinuationCapability } from './continuations.ts';
import type {
  ThreadProposalHandoff,
  ToolHandoffOrigin,
  ToolLaunchContext,
} from './continuations.ts';
import type { CopilotContractKey } from './copilot.ts';
import type {
  SkillKey,
  SkillMissingRequirementKind,
  SkillReadScope,
} from './skills.ts';

export const PREBUILT_TOOL_KEYS = [
  'analyze_call',
  'draft_followup',
  'prepare_meeting',
  'pipeline_health_check',
  'qualify_account',
] as const;

export type PrebuiltToolKey = typeof PREBUILT_TOOL_KEYS[number];
export type PrebuiltToolPackKey = 'domino_sales_core';
export type ToolPageMode = 'diagnostic' | 'artifact' | 'data';

export type TenantPrebuiltToolStatus = 'pending' | 'ready' | 'disabled' | 'error';

export interface PrebuiltToolDefinition {
  key: PrebuiltToolKey;
  title: string;
  pageMode: ToolPageMode;
  inputCapability?: ToolContinuationCapability | null;
  templateKey: string;
  skillKey: SkillKey;
  copilotContractKey: CopilotContractKey;
  packKey: PrebuiltToolPackKey;
  packVersion: string;
  workspaceKind: 'shared';
  defaultReadScope: SkillReadScope;
}

export interface TenantPrebuiltToolRecord {
  id: string;
  tenant_id: string;
  tool_key: PrebuiltToolKey;
  skill_key: SkillKey;
  template_key: string;
  pack_key: PrebuiltToolPackKey;
  pack_version: string;
  installed_flow_id: string | null;
  workspace_dashboard_id: string | null;
  status: TenantPrebuiltToolStatus;
  config: Record<string, unknown>;
  last_bootstrapped_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface BootstrapPrebuiltToolsRequest {
  toolKeys?: PrebuiltToolKey[];
}

export interface BootstrapPrebuiltToolsResponse {
  tools: TenantPrebuiltToolRecord[];
}

export type ToolRuntimeSubmission =
  | {
    kind: 'paste_transcript';
    transcript: string;
  }
  | {
    kind: 'provide_inputs';
    inputValues: Record<string, string>;
  }
  | {
    kind: 'choose_call';
    callId: string;
  };

export interface ToolRuntimeMissingRequirement {
  kind: SkillMissingRequirementKind;
  title: string;
  description: string;
  ctaLabel: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  isConnector?: boolean;
}

interface ToolRuntimeBaseState {
  toolKey: string;
  toolKind?: 'prebuilt' | 'flow';
  dashboardId: string | null;
  workspaceTitle: string | null;
  flowId: string | null;
}

export interface ToolRuntimeReadyImmediatelyState extends ToolRuntimeBaseState {
  status: 'ready_immediately';
  flowId: string;
  ctaLabel: string;
  sourceLabel?: string | null;
}

export interface ToolRuntimeMissingOneThingState extends ToolRuntimeBaseState {
  status: 'missing_one_thing';
  requirement: ToolRuntimeMissingRequirement;
}

export interface ToolRuntimeRunningState extends ToolRuntimeBaseState {
  status: 'running';
  flowId: string;
  executionId: string;
  executionStatus: 'pending' | 'running';
}

export interface ToolRuntimeBlockedState extends ToolRuntimeBaseState {
  status: 'blocked';
  flowId: string;
  executionId: string;
  executionStatus: 'paused';
}

export interface ToolRuntimeWorkspaceReadyState extends ToolRuntimeBaseState {
  status: 'workspace_ready';
  flowId: string;
  executionId: string | null;
}

export type ToolRuntimePublicState =
  | ToolRuntimeReadyImmediatelyState
  | ToolRuntimeMissingOneThingState
  | ToolRuntimeRunningState
  | ToolRuntimeBlockedState
  | ToolRuntimeWorkspaceReadyState;

export interface ResolveToolRuntimeRequest {
  toolKey?: PrebuiltToolKey;
  flowId?: string;
  submission?: ToolRuntimeSubmission | null;
  clientContext?: DashboardActionClientContext;
  handoffOrigin?: ToolHandoffOrigin | null;
  proposalHandoff?: ThreadProposalHandoff | null;
  launchContext?: ToolLaunchContext | null;
}
